import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { WarningIcon } from 'src/Libs/flow/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { localizeEmobilityNumber } from '../utils/localize-emobility-number'
import { HagerTablePaginationActions } from '../Overview/HagerTablePaginationActions'
import { AuthenticationInfo } from '../AuthenticationInfo'
import { MIDCertificationMarker, PROAuthenticationMarker } from '../Marker'
import { ChargingSessionModel } from '../charging-session-models'
import { getSessionStatusTranslation } from '../charging-session-parser'
import { setSessionsForExport } from '../sessions-for-export'
import { formatDuration } from '../utils/format-duration'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { ChargingSessionsSolarRatioComponent } from './ChargingSessionsSolarRatioComponent'
import useModals from 'src/Hooks/useModals'

type SessionsTableColumnConfig = {
  id: keyof ChargingSessionModel
  label: React.ReactNode
  sortFunction: (item1: ChargingSessionModel, item2: ChargingSessionModel) => number
  valueRender: (item: ChargingSessionModel) => React.ReactNode
}

type ArrowDirection = 'left' | 'right' | 'indeterminate'

interface ChargingSessionTableProps {
  installationId: number
  chargingSessions: ChargingSessionModel[]
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rows: number, sessionsCount: number) => void
  sort: { name: keyof ChargingSessionModel; direction: 'asc' | 'desc' }
  setSort: (name: keyof ChargingSessionModel) => void
}
export const ChargingSessionTable: React.FC<ChargingSessionTableProps> = ({
  installationId,
  chargingSessions,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  sort,
  setSort,
}): React.ReactElement => {
  const { t } = useCustomTranslation()

  const { openChargingSessionDetailsDialogV1Flow } = useModals()

  const tableContainerRef = useRef<HTMLElement | null>(null)
  const [hasScrollbar, setHasScrollbar] = useState<boolean>(false)
  const [scrollDirection, setScrollDirection] = useState<ArrowDirection>('left')

  const showArrows = (): void => {
    if (!tableContainerRef.current) return
    const el = tableContainerRef.current
    setHasScrollbar(el.scrollWidth > el.clientWidth)
  }

  const followScrollBar = (event: Event): void => {
    const eventTarget = event.target as HTMLElement
    const elScroll = eventTarget.scrollLeft
    if (elScroll === 0) {
      setScrollDirection('left')
    } else if (eventTarget.scrollWidth <= Math.ceil(elScroll + eventTarget.offsetWidth)) {
      setScrollDirection('right')
    } else {
      setScrollDirection('indeterminate')
    }
  }

  useEffect(() => {
    const el = tableContainerRef.current
    showArrows()
    el?.addEventListener('scroll', followScrollBar)
    window.addEventListener('resize', showArrows)
    return () => {
      window.removeEventListener('resize', showArrows)
      el?.removeEventListener('scroll', followScrollBar)
    }
  }, [])

  const onArrowClick = (direction: ArrowDirection): void => {
    if (!tableContainerRef.current) return
    const el = tableContainerRef.current
    const scrollAmount = direction === 'right' ? el.scrollWidth : 0
    el.scroll({
      left: scrollAmount,
      behavior: 'smooth',
    })
    setScrollDirection(direction)
  }

  const sessionsTableConfig: SessionsTableColumnConfig[] = useMemo(
    () => [
      {
        id: 'startAt',
        label: t('emobility.chargingSession.startDate'),
        valueRender: ({ startAt }) => (startAt ? moment.utc(startAt).local().format(t('common.format.dateTime')) : ''),
        sortFunction: nullsLast((a, b) => (a.startAt && b.startAt ? a.startAt.localeCompare(b.startAt) : 0)),
      },
      {
        id: 'status',
        label: t('emobility.chargingSession.status'),
        valueRender: ({ status, midAnomaly }) => (
          <>
            {getSessionStatusTranslation(t, status)}
            {midAnomaly && (
              <Box>
                <WarningIcon color="warning" sx={{ float: 'left' }} />
                {t('emobility.chargingSession.charge-measurement-anomaly')}
              </Box>
            )}
          </>
        ),
        sortFunction: (a, b) => getSessionStatusTranslation(t, a.status).localeCompare(getSessionStatusTranslation(t, b.status)),
      },
      {
        id: 'durationMilliseconds',
        label: t('emobility.chargingSession.duration'),
        valueRender: ({ durationMilliseconds }) => formatDuration(durationMilliseconds, t),
        sortFunction: (a, b) => a.durationMilliseconds - b.durationMilliseconds,
      },
      {
        id: 'energyAll',
        label: t('emobility.chargingSession.totalEnergy'),
        valueRender: ({ energyAll, isMidCertified }) => (
          <>
            {t('common.format.kwh', '', { value: localizeEmobilityNumber(energyAll / 1000) })} {isMidCertified && <MIDCertificationMarker />}
          </>
        ),
        sortFunction: (a, b) => a.energyAll - b.energyAll,
      },
      {
        id: 'energySolar',
        label: t('emobility.chargingSession.solarEnergy'),
        valueRender: ({ energySolar }) => t('common.format.kwh', '', { value: localizeEmobilityNumber(energySolar / 1000) }),
        sortFunction: (a, b) => a.energySolar - b.energySolar,
      },
      {
        id: 'solarRatio',
        label: t('emobility.chargingSession.solarRatio'),
        valueRender: ({ solarRatio }) => <ChargingSessionsSolarRatioComponent ratio={solarRatio} />,
        sortFunction: (a, b) => a.solarRatio - b.solarRatio,
      },
      {
        id: 'authentication',
        label: t('emobility.chargingSession.authentication'),
        valueRender: ({ authentication }) => (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <AuthenticationInfo rfid={authentication.data ?? null} cardName={authentication.cardName} />
            {authentication.type === 'OCPP' ? <PROAuthenticationMarker /> : null}
          </Stack>
        ),
        sortFunction: (a, b) =>
          `${t(`emobility.chargingSession.authType.${a.authentication.type}`)}${a.authentication.data}}`?.localeCompare(
            `${t(`emobility.chargingSession.authType.${b.authentication.type}`)}${b.authentication.data}`
          ),
      },
      {
        id: 'deviceName',
        label: t('emobility.chargingSession.chargingStation'),
        valueRender: ({ deviceName }) => deviceName,
        sortFunction: (a, b) => a.deviceName.localeCompare(b.deviceName),
      },
    ],
    [t]
  )

  const sortFunction = sessionsTableConfig.find(config => config.id === sort.name)?.sortFunction
  const sortDirection = sort.direction
  const sortedSessions = useMemo(() => {
    if (!sortFunction) return chargingSessions

    const sortedAscending = chargingSessions.sort(sortFunction)
    if (sortDirection === 'asc') {
      return sortedAscending
    } else {
      return sortedAscending.reverse()
    }
  }, [chargingSessions, sortFunction, sortDirection])

  setSessionsForExport(sortedSessions)

  const paginatedSessions = sortedSessions.slice(page * rowsPerPage, (page + 1) * rowsPerPage)

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10), chargingSessions.length)
  }

  return (
    <Paper
      sx={{
        position: 'relative',
        px: 3,
      }}
    >
      {hasScrollbar && (
        <>
          <IconButton
            disableRipple
            onClick={() => onArrowClick('left')}
            disabled={scrollDirection === 'left'}
            size="small"
            color="inherit"
            sx={{
              position: 'absolute',
              left: 0,
              top: 38,
              width: 30,
              height: 30,
              p: '6px',
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
          </IconButton>

          <IconButton
            disableRipple
            onClick={() => onArrowClick('right')}
            disabled={scrollDirection === 'right'}
            size="small"
            color="inherit"
            sx={{
              position: 'absolute',
              right: 0,
              top: 38,
              width: 30,
              height: 30,
              p: '6px',
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </>
      )}

      <TableContainer
        ref={el => {
          tableContainerRef.current = el
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {sessionsTableConfig.map(config => (
                <SessionTableHeader
                  key={config.id}
                  label={config.label}
                  sorting={config.id === sort.name ? sort.direction : null}
                  onRequestSort={() => setSort(config.id)}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!paginatedSessions.length ? (
              <TableRow>
                <TableCell colSpan={sessionsTableConfig.length} align="center">
                  {t('shell.noData')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedSessions.map(session => (
                <TableRow key={session.id} hover role="button" sx={{ cursor: 'pointer' }}>
                  {sessionsTableConfig.map(config => (
                    <TableCell
                      key={config.id}
                      onClick={() =>
                        openChargingSessionDetailsDialogV1Flow({
                          installationId,
                          session,
                        })
                      }
                    >
                      {config.valueRender(session)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component={'div'}
        count={chargingSessions.length}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[3, 5, 10, 25, 50]}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={props => <HagerTablePaginationActions {...props} pageLabelSpacing={5} />}
        labelRowsPerPage={t('pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('pagination.from')} ${count}`}
      />
    </Paper>
  )
}

interface SessionTableHeaderProps {
  label: React.ReactNode
  sorting: 'asc' | 'desc' | null
  onRequestSort: () => void
}
const SessionTableHeader: React.FC<SessionTableHeaderProps> = ({ label, sorting, onRequestSort }) => {
  return (
    <TableCell sortDirection={sorting ?? undefined}>
      <TableSortLabel
        active={!!sorting}
        direction={sorting ?? undefined}
        onClick={onRequestSort}
        sx={{ textTransform: 'uppercase', whiteSpace: 'pre-line' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )
}
